import { useState, useEffect, useRef } from 'react';
import { chainAPI, mediaAPI, narrationAPI } from '../lib/api';
import { Upload, Music, Trash2, FileImage, Loader2, AlertCircle, Mic, Sparkles, Play, Pause, RefreshCw, CheckCircle2 } from 'lucide-react';

const STEP_KEYS = [
  { key: 'origin', label: 'Origin' },
  { key: 'change_a', label: 'Change A' },
  { key: 'change_b', label: 'Change B' },
  { key: 'contradiction', label: 'Contradiction' },
  { key: 'modern_pattern', label: 'Modern Pattern' },
];

function formatBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function ImageUploader({ chainId, stepKey, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [caption, setCaption] = useState('');
  const [source, setSource] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('chain_id', chainId);
      fd.append('step_key', stepKey);
      fd.append('caption', caption);
      fd.append('source', source);
      fd.append('order_index', '0');
      const res = await mediaAPI.uploadImage(fd, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      onUploaded?.(res.data);
      setCaption('');
      setSource('');
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 600);
    }
  };

  return (
    <div className="space-y-2" data-testid={`image-uploader-${stepKey}`}>
      <div className="flex gap-2">
        <input
          data-testid={`image-caption-${stepKey}`}
          className="hud-input text-xs flex-1"
          placeholder="Caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          disabled={uploading}
        />
        <input
          data-testid={`image-source-${stepKey}`}
          className="hud-input text-xs flex-1"
          placeholder="Source citation"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          disabled={uploading}
        />
      </div>
      <label
        htmlFor={`upload-img-${stepKey}`}
        className={`flex items-center justify-center gap-2 px-3 py-2 border border-dashed rounded text-[10px] font-hud tracking-wider uppercase cursor-pointer transition-all duration-300 ${
          uploading
            ? 'border-[#D4A84A]/40 text-[#D4A84A] bg-[#D4A84A]/5'
            : 'border-[#2F8A92]/25 text-[#2F8A92] hover:bg-[#2F8A92]/5'
        }`}
      >
        {uploading ? (
          <>
            <Loader2 size={12} className="animate-spin" /> UPLOADING {progress}%
          </>
        ) : (
          <>
            <Upload size={12} /> CHOOSE IMAGE (JPG / PNG / HEIC — auto WebP, 1920px)
          </>
        )}
      </label>
      <input
        id={`upload-img-${stepKey}`}
        data-testid={`image-input-${stepKey}`}
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        disabled={uploading}
      />
      {error && (
        <p className="flex items-center gap-1 text-[10px] text-red-400 font-mono">
          <AlertCircle size={10} /> {error}
        </p>
      )}
    </div>
  );
}

function AudioUploader({ chainId, stepKey, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('chain_id', chainId);
      fd.append('step_key', stepKey);
      fd.append('is_narration', 'true');
      const res = await mediaAPI.uploadAudio(fd, (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      });
      onUploaded?.(res.data);
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 600);
    }
  };

  return (
    <div className="space-y-2" data-testid={`audio-uploader-${stepKey}`}>
      <label
        htmlFor={`upload-audio-${stepKey}`}
        className={`flex items-center justify-center gap-2 px-3 py-2 border border-dashed rounded text-[10px] font-hud tracking-wider uppercase cursor-pointer transition-all duration-300 ${
          uploading
            ? 'border-[#D4A84A]/40 text-[#D4A84A] bg-[#D4A84A]/5'
            : 'border-[#D4A84A]/25 text-[#D4A84A] hover:bg-[#D4A84A]/5'
        }`}
      >
        {uploading ? (
          <>
            <Loader2 size={12} className="animate-spin" /> UPLOADING {progress}%
          </>
        ) : (
          <>
            <Music size={12} /> UPLOAD NARRATION MP3
          </>
        )}
      </label>
      <input
        id={`upload-audio-${stepKey}`}
        data-testid={`audio-input-${stepKey}`}
        ref={fileRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={handleFile}
        disabled={uploading}
      />
      {error && (
        <p className="flex items-center gap-1 text-[10px] text-red-400 font-mono">
          <AlertCircle size={10} /> {error}
        </p>
      )}
    </div>
  );
}

function StepMediaPanel({ chain, chainId, stepKey, stepLabel, mediaList, hasNarrator, voices, previewStatus, onChanged }) {
  const stepImages = mediaList.filter((m) => m.step_key === stepKey && m.media_type === 'image');
  const stepAudio = mediaList.filter((m) => m.step_key === stepKey && m.media_type === 'audio');
  const hasNarration = stepAudio.some((a) => a.is_narration);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [savingVoice, setSavingVoice] = useState(false);

  const stepDoc = chain?.[stepKey] || {};
  const stepVoiceId = stepDoc.narrator_voice_id || '';
  const effectiveVoiceId = stepVoiceId || chain?.narrator_voice_id || '';
  const effectiveVoice = voices.find((v) => v.voice_id === effectiveVoiceId);
  const usingOverride = !!stepVoiceId;

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this media?')) return;
    await mediaAPI.delete(id);
    onChanged?.();
  };

  const handleGenerate = async () => {
    setGenError('');
    setGenerating(true);
    try {
      await narrationAPI.generate(chainId, stepKey);
      onChanged?.();
    } catch (err) {
      setGenError(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSetStepVoice = async (newVoiceId) => {
    setSavingVoice(true);
    try {
      const v = voices.find((x) => x.voice_id === newVoiceId);
      await narrationAPI.setStepNarrator(chainId, stepKey, newVoiceId || null, v?.name || null);
      onChanged?.();
    } finally {
      setSavingVoice(false);
    }
  };

  return (
    <div
      className="border border-[#2F8A92]/10 rounded p-3 space-y-3"
      data-testid={`step-media-${stepKey}`}
    >
      <div className="flex items-center justify-between">
        <h5 className="font-hud text-xs tracking-widest text-[#2F8A92] uppercase">
          {stepLabel}
        </h5>
        <span className="font-mono text-[9px] text-[#52525B]">
          {stepImages.length} img · {stepAudio.length} audio
        </span>
      </div>

      {/* Per-step voice override (Build Fest Idea 3 — voice-shift) */}
      {hasNarrator && voices.length > 0 && (
        <div
          className="flex items-center gap-2 text-[10px]"
          data-testid={`step-voice-override-${stepKey}`}
        >
          <span className="font-hud tracking-wider text-[#D4A84A] uppercase flex-shrink-0">VOICE:</span>
          <select
            data-testid={`step-voice-select-${stepKey}`}
            value={stepVoiceId}
            onChange={(e) => handleSetStepVoice(e.target.value)}
            disabled={savingVoice}
            className="hud-input text-[10px] flex-1"
          >
            <option value="">
              {chain?.narrator_voice_name
                ? `— inherit (${chain.narrator_voice_name}) —`
                : '— inherit chain default —'}
            </option>
            {voices.map((v) => (
              <option key={v.voice_id} value={v.voice_id}>
                {previewStatus?.[v.voice_id] ? '▶ ' : ''}{v.name}
              </option>
            ))}
          </select>
          {usingOverride && effectiveVoice && (
            <span
              className="font-hud text-[8px] tracking-wider uppercase px-1.5 py-0.5 rounded border border-[#D4A84A]/30 text-[#D4A84A] flex-shrink-0"
              title={`Effective narrator for this step: ${effectiveVoice.name}`}
            >
              OVERRIDE
            </span>
          )}
        </div>
      )}

      {/* Existing images */}
      {stepImages.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {stepImages.map((img) => (
            <div
              key={img.id}
              className="relative group aspect-video overflow-hidden border border-[#2F8A92]/15 rounded"
              data-testid={`media-thumb-${img.id}`}
            >
              <img
                src={mediaAPI.fileUrl(img.id)}
                alt={img.caption || img.original_filename}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                <div className="text-[8px] font-mono text-[#D4A84A] truncate">
                  {img.caption || img.original_filename}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[8px] font-mono text-white/80">
                    {img.width}×{img.height} · {formatBytes(img.size)}
                  </span>
                  <button
                    data-testid={`delete-media-${img.id}`}
                    onClick={() => handleDelete(img.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Existing audio */}
      {stepAudio.length > 0 && (
        <div className="space-y-1">
          {stepAudio.map((a) => (
            <div
              key={a.id}
              className="flex items-center gap-2 px-2 py-1.5 border border-[#D4A84A]/15 rounded"
              data-testid={`audio-item-${a.id}`}
            >
              <Music size={12} className={a.is_narration ? 'text-[#D4A84A]' : 'text-white/80'} />
              <span className="font-mono text-[10px] text-white/80 truncate flex-1">
                {a.is_narration && <span className="text-[#D4A84A]">[NARRATION] </span>}
                {a.original_filename}
              </span>
              <audio src={mediaAPI.fileUrl(a.id)} controls className="h-6" style={{ maxWidth: '160px' }} />
              <span className="font-mono text-[9px] text-[#52525B]">{formatBytes(a.size)}</span>
              <button
                data-testid={`delete-audio-${a.id}`}
                onClick={() => handleDelete(a.id)}
                className="text-[#52525B] hover:text-red-400"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Generate narration via ElevenLabs — only if chain has voice assigned and no cached narration yet */}
      {hasNarrator && !hasNarration && (
        <button
          data-testid={`generate-narration-${stepKey}`}
          onClick={handleGenerate}
          disabled={generating}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-[#D4A84A]/40 rounded font-hud text-[10px] tracking-wider text-[#D4A84A] hover:bg-[#D4A84A]/5 transition-all duration-300 disabled:opacity-50"
        >
          {generating ? (
            <>
              <Loader2 size={12} className="animate-spin" /> GENERATING VIA ELEVENLABS…
            </>
          ) : (
            <>
              <Sparkles size={12} /> GENERATE NARRATION (ELEVENLABS)
            </>
          )}
        </button>
      )}
      {genError && (
        <p className="flex items-center gap-1 text-[10px] text-red-400 font-mono">
          <AlertCircle size={10} /> {genError}
        </p>
      )}

      {/* Uploaders */}
      <ImageUploader chainId={chainId} stepKey={stepKey} onUploaded={() => onChanged?.()} />
      <AudioUploader chainId={chainId} stepKey={stepKey} onUploaded={() => onChanged?.()} />
    </div>
  );
}

function VoicePreviewButton({ voiceId, cached }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!cached) return;
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`voice-preview-${voiceId}`}
        onClick={toggle}
        disabled={!cached}
        title={cached ? 'Play voice preview' : 'Preview not cached yet'}
        className={`flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full border transition-all ${
          cached
            ? 'border-[#D4A84A]/40 text-[#D4A84A] hover:bg-[#D4A84A]/10'
            : 'border-[#52525B]/30 text-[#52525B] cursor-not-allowed'
        }`}
      >
        {playing ? <Pause size={10} /> : <Play size={10} />}
      </button>
      {cached && (
        <audio
          ref={audioRef}
          src={narrationAPI.previewUrl(voiceId)}
          onEnded={() => setPlaying(false)}
          preload="none"
        />
      )}
    </>
  );
}

function NarratorPicker({ chain, onSaved }) {
  const [voices, setVoices] = useState([]);
  const [previewStatus, setPreviewStatus] = useState({}); // voice_id -> true if cached
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [voiceId, setVoiceId] = useState(chain?.narrator_voice_id || '');
  const [loaded, setLoaded] = useState(false);
  const [caching, setCaching] = useState(false);
  const [cacheResult, setCacheResult] = useState(null);

  useEffect(() => {
    setVoiceId(chain?.narrator_voice_id || '');
  }, [chain?.id, chain?.narrator_voice_id]);

  const loadPreviewStatus = async () => {
    try {
      const r = await narrationAPI.previewStatus();
      const map = {};
      for (const p of r.data || []) map[p.voice_id] = true;
      setPreviewStatus(map);
    } catch {}
  };

  const loadVoices = async () => {
    if (loaded) return;
    setLoading(true);
    setError('');
    try {
      const [vres] = await Promise.all([narrationAPI.voices(), loadPreviewStatus()]);
      setVoices(vres.data || []);
      setLoaded(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load voices');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!voiceId) return;
    setSaving(true);
    setError('');
    try {
      const voice = voices.find((v) => v.voice_id === voiceId);
      await chainAPI.setNarrator(chain.id, voiceId, voice?.name || '');
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save narrator');
    } finally {
      setSaving(false);
    }
  };

  const handleBatchCache = async () => {
    if (!window.confirm('Generate a ~200-char preview for every voice that has not been cached yet? Approx $0.06 per new voice.')) return;
    setCaching(true);
    setCacheResult(null);
    try {
      const r = await narrationAPI.cachePreviews();
      setCacheResult(r.data);
      await loadPreviewStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Batch cache failed');
    } finally {
      setCaching(false);
    }
  };

  const selectedCached = !!previewStatus[voiceId];

  return (
    <div
      className="border border-[#D4A84A]/15 rounded p-3 space-y-2"
      data-testid="narrator-picker"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <Mic size={12} className="text-[#D4A84A]" />
        <h4 className="font-hud text-[11px] tracking-widest text-[#D4A84A] uppercase">
          Chain Narrator
        </h4>
        {chain?.narrator_voice_name && (
          <span className="font-mono text-[9px] text-white/80">
            Current: <span className="text-[#D4A84A]">{chain.narrator_voice_name}</span>
          </span>
        )}
        <div className="ml-auto">
          <button
            data-testid="batch-cache-voices-btn"
            type="button"
            onClick={handleBatchCache}
            disabled={caching || !loaded}
            title={loaded ? 'Batch-cache a preview for every voice' : 'Load voices first'}
            className="font-hud text-[9px] tracking-wider text-[#2F8A92] border border-[#2F8A92]/30 px-2 py-1 hover:bg-[#2F8A92]/10 disabled:opacity-40 flex items-center gap-1"
          >
            {caching ? <Loader2 size={9} className="animate-spin" /> : <RefreshCw size={9} />}
            CACHE PREVIEWS
          </button>
        </div>
      </div>

      <div className="flex gap-2 items-center">
        {voiceId && (
          <VoicePreviewButton voiceId={voiceId} cached={selectedCached} />
        )}
        <select
          data-testid="narrator-voice-select"
          value={voiceId}
          onClick={loadVoices}
          onFocus={loadVoices}
          onChange={(e) => setVoiceId(e.target.value)}
          disabled={loading}
          className="hud-input text-xs flex-1"
        >
          <option value="">
            {loading ? 'Loading voices…' : loaded ? '— Select voice —' : 'Click to load voices'}
          </option>
          {voices.map((v) => (
            <option key={v.voice_id} value={v.voice_id}>
              {previewStatus[v.voice_id] ? '▶ ' : ''}
              {v.name} {v.labels?.description ? `· ${v.labels.description}` : ''}
            </option>
          ))}
        </select>
        <button
          data-testid="save-narrator-btn"
          type="button"
          onClick={handleSave}
          disabled={!voiceId || saving}
          className="font-hud text-[10px] tracking-wider text-[#0A0A0A] bg-[#D4A84A] px-3 py-1.5 hover:bg-[#D4A84A]/80 disabled:opacity-40 transition-all duration-300"
        >
          {saving ? 'SAVING…' : 'SET NARRATOR'}
        </button>
      </div>

      {cacheResult && (
        <p className="font-mono text-[9px] text-white/80 flex items-center gap-1" data-testid="batch-cache-result">
          <CheckCircle2 size={10} className="text-[#2F8A92]" />
          Generated {cacheResult.generated?.length || 0} · Skipped {cacheResult.skipped?.length || 0} · Errors {cacheResult.errors?.length || 0} · Total voices {cacheResult.total_voices}
        </p>
      )}
      {error && (
        <p className="flex items-center gap-1 text-[10px] text-red-400 font-mono">
          <AlertCircle size={10} /> {error}
        </p>
      )}
      <p className="font-mono text-[9px] text-[#52525B]">
        ▶ prefix means preview cached — audition the voice before locking a chain. Use CACHE PREVIEWS to batch-generate a sample in every available voice.
      </p>
    </div>
  );
}

export default function MediaManager() {
  const [chains, setChains] = useState([]);
  const [selectedChainId, setSelectedChainId] = useState('');
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [voices, setVoices] = useState([]);
  const [previewStatus, setPreviewStatus] = useState({});

  const fetchChains = () => {
    chainAPI.list().then((r) => {
      setChains(r.data);
      if (r.data.length > 0 && !selectedChainId) {
        setSelectedChainId(r.data[0].id);
      }
    });
  };

  const fetchVoices = () => {
    narrationAPI.voices().then((r) => setVoices(r.data || [])).catch(() => {});
    narrationAPI.previewStatus().then((r) => {
      const map = {};
      for (const p of r.data || []) map[p.voice_id] = true;
      setPreviewStatus(map);
    }).catch(() => {});
  };

  useEffect(() => {
    fetchChains();
    fetchVoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMedia = () => {
    if (!selectedChainId) return;
    setLoading(true);
    mediaAPI
      .listChain(selectedChainId)
      .then((r) => setMediaList(r.data))
      .catch(() => setMediaList([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChainId]);

  const selectedChain = chains.find((c) => c.id === selectedChainId);
  const hasNarrator = !!selectedChain?.narrator_voice_id;

  return (
    <div className="space-y-4" data-testid="admin-media-manager">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <h3 className="font-hud text-[#2F8A92] text-sm tracking-widest uppercase flex items-center gap-2">
          <FileImage size={14} /> CHAIN MEDIA
        </h3>
        <select
          data-testid="media-chain-select"
          value={selectedChainId}
          onChange={(e) => setSelectedChainId(e.target.value)}
          className="hud-input text-xs min-w-[240px]"
        >
          {chains.map((c) => (
            <option key={c.id} value={c.id}>
              {c.subject}
            </option>
          ))}
        </select>
      </div>

      {selectedChain && (
        <p className="font-mono text-[10px] text-[#52525B]">
          Editing media for <span className="text-[#D4A84A]">{selectedChain.subject}</span>. Images
          are auto-resized to 1920px and converted to WebP. Narration mp3 files are stored as
          uploaded.
        </p>
      )}

      {selectedChain && (
        <NarratorPicker chain={selectedChain} onSaved={fetchChains} />
      )}

      {loading ? (
        <p className="font-hud text-xs text-white/80 tracking-wider animate-pulse">LOADING MEDIA…</p>
      ) : selectedChainId ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {STEP_KEYS.map((s) => (
            <StepMediaPanel
              key={s.key}
              chain={selectedChain}
              chainId={selectedChainId}
              stepKey={s.key}
              stepLabel={s.label}
              mediaList={mediaList}
              hasNarrator={hasNarrator}
              voices={voices}
              previewStatus={previewStatus}
              onChanged={() => { loadMedia(); fetchChains(); }}
            />
          ))}
        </div>
      ) : (
        <p className="font-mono text-xs text-[#52525B]">No chains available.</p>
      )}
    </div>
  );
}
